import React from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { InlineDataTable, HeaderRow, TableHeader } from 'uiComponents/table'
import { NoResultsRow } from 'uiComponents/table/noResultsRow'
import { NestedContainer, NestedElement } from 'products/components/nestedContainer'
import { ExpansionProvider } from 'products/components/expansionContext'
import { PricingService } from 'products/pricing/pricingService'
import { Checkbox } from 'uiComponents/input'
import { Navigation, parseSearch } from 'navigation'
import { MessageKind } from 'uiComponents/messages'
import { ProductListWithPath } from 'products/crud/common'
import { ProductList } from 'admin/articleService'
import { PricingType } from 'products/articleConfigurationService'
import { useTranslation } from 'translations'

interface ListTableProps {
    hasPermission: (permission: string, accountSlug: string) => boolean
    elements: NestedElement[]
    accountSlug: string
    pricingService: PricingService
    navigation: Navigation
    flat?: boolean
    deleteArticle: (id: string) => void
    deleteProductList: (id: string) => void
    duplicateProduct?: (id: string, name: string) => void
    updateProductListStatusAndPriority?: (id: string, enabled: boolean, priority: number) => void
    updateArticlePriority?: (id: string, priority: number) => void
    updateArticleStatus?: (id: string, enabled: boolean) => void
    hasFeature: (feature: string, slug: string) => boolean
    replaceTopMessages?: (id: string, status: MessageKind, text: string) => void
    hideMessage?: (id: string) => void
    removeAllMessages?: () => void
    flattenedCategories: ProductListWithPath[] | null
    accountCategories: ProductList[]
    setAccountCategories?: (cats: ProductList[]) => void
    orphans?: boolean
    backgroundLoading?: boolean
    origin?: '?from=flat' | '?from=nested' | ''
    showBulkEditButtons?: (v: boolean) => void
    bulkEditPricing?: boolean
    bulkEditValidity?: boolean
    cancelBulkEditPricing?: () => void
    cancelBulkEditValidity?: () => void
    updateArticleOriginalPrice?: (
        id: string,
        price: string,
        min: number | null,
        avg: number | null,
        gate: number | null,
        stepSize: string | null,
        charmPricing: boolean | null,
        pricingType: PricingType,
    ) => void
    refreshArticles?: () => void
    orphanArticlesExist?: boolean
}

function ProductListsTable(props: ListTableProps & RouteComponentProps<{}>) {
    const { t } = useTranslation()
    const columnWidths =
        props.origin === '?from=flat'
            ? ['1em', '3em', null, '7em', '4.7em', '6.5em', '6.5em', '9.2em', '15em', '.5em']
            : ['3em', null, '7em', '4.7em', '6.5em', '6.5em', '9.2em', '15em', '.5em']

    const {
        elements,
        flat,
        pricingService,
        history,
        deleteArticle,
        deleteProductList,
        updateProductListStatusAndPriority,
        updateArticlePriority,
        updateArticleStatus,
        replaceTopMessages,
        hideMessage,
        removeAllMessages,
        origin,
        duplicateProduct,
    } = props

    const [allChecked, setAllChecked] = React.useState<boolean>(false)
    const previousPricingTypesRef = React.useRef<string | undefined>(undefined)
    const previousAccountSlugRef = React.useRef<string | undefined>(undefined)

    // Recalculate openElementIds whenever the URL changes
    // BUT: if pricingTypes or accountSlug changed, ignore listIds and clear the URL
    const openElementIds = React.useMemo(() => {
        const search = props.location.search
        const params = parseSearch(search)
        const currentPricingTypes = params.pricingTypes
        const currentAccountSlug = props.accountSlug

        // Check if pricingTypes or accountSlug changed
        const pricingTypesChanged = previousPricingTypesRef.current !== undefined &&
                                    previousPricingTypesRef.current !== currentPricingTypes
        const accountSlugChanged = previousAccountSlugRef.current !== undefined &&
                                   previousAccountSlugRef.current !== currentAccountSlug

        // Update the refs
        previousPricingTypesRef.current = currentPricingTypes
        previousAccountSlugRef.current = currentAccountSlug

        // If either changed, clear listIds from URL and return empty array
        if (pricingTypesChanged || accountSlugChanged) {
            // Remove listIds from URL
            const { listIds, ...restParams } = params
            const queryParts: string[] = []
            Object.entries(restParams).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                }
            })
            const newSearch = queryParts.join('&')
            window.history.replaceState(null, '', `${props.location.pathname}${newSearch ? '?' + newSearch : ''}`)
            return []
        }

        // Otherwise, parse and return listIds from URL
        return params.listIds?.split(',').filter(id => id) || []
    }, [props.location.search, props.accountSlug])

    // Create a key from pricingTypes and accountSlug to force remount when they change
    const providerKey = `${props.accountSlug}-${parseSearch(props.location.search).pricingTypes || 'all'}`

    return (
        <ExpansionProvider key={providerKey} navigation={props.navigation} initialPath={openElementIds}>
            <InlineDataTable columnWidths={columnWidths} id="products-list-page" bordered={!flat}>
                <HeaderRow>
                    {props.origin === '?from=flat' && (
                        <TableHeader nonInteractive>
                            <Checkbox
                                id="selectArticle"
                                name="selectArticles"
                                checked={allChecked}
                                onChange={(e) => {
                                    setAllChecked(e.target.checked)
                                    if (props.showBulkEditButtons) {
                                        props.showBulkEditButtons(e.target.checked)
                                    }
                                }}
                            />
                        </TableHeader>
                    )}
                    <TableHeader nonInteractive />
                    <TableHeader>{t('products.nestedTable.columns.name')}</TableHeader>
                    <TableHeader>{t('products.nestedTable.columns.priority')}</TableHeader>
                    <TableHeader>{t('products.nestedTable.columns.type')}</TableHeader>
                    <TableHeader align="right" style={{ whiteSpace: 'nowrap' }}>
                        {t('products.nestedTable.columns.minPrice')}
                    </TableHeader>
                    <TableHeader align="right" style={{ whiteSpace: 'nowrap' }}>
                        {t('products.nestedTable.columns.maxPrice')}
                    </TableHeader>
                    <TableHeader>{t('products.nestedTable.columns.validity')}</TableHeader>
                    <TableHeader nonInteractive />
                    <TableHeader nonInteractive noPadding />
                </HeaderRow>
                <NestedContainer
                    containerType="product"
                    expanded={false}
                    elements={elements}
                    flattenedCategories={props.flattenedCategories}
                    accountCategories={props.accountCategories}
                    orphans={props.orphans}
                    pricingService={pricingService}
                    accountSlug={props.accountSlug}
                    hasPermission={props.hasPermission}
                    grade={0}
                    flat={flat}
                    history={history}
                    deleteArticle={deleteArticle}
                    deleteProductList={deleteProductList}
                    duplicateProduct={duplicateProduct}
                    updateProductListStatusAndPriority={updateProductListStatusAndPriority}
                    updateArticlePriority={updateArticlePriority}
                    updateArticleStatus={updateArticleStatus}
                    replaceTopMessages={replaceTopMessages}
                    hideMessage={hideMessage}
                    removeAllMessages={removeAllMessages}
                    hasFeature={props.hasFeature}
                    setAccountCategories={props.setAccountCategories}
                    backgroundLoading={props.backgroundLoading}
                    origin={origin}
                    allChecked={allChecked}
                    setAllChecked={setAllChecked}
                    showBulkEditButtons={props.showBulkEditButtons}
                    bulkEditPricing={props.bulkEditPricing}
                    bulkEditValidity={props.bulkEditValidity}
                    cancelBulkEditPricing={props.cancelBulkEditPricing}
                    cancelBulkEditValidity={props.cancelBulkEditValidity}
                    updateArticleOriginalPrice={props.updateArticleOriginalPrice}
                    refreshArticles={props.refreshArticles}
                    navigation={props.navigation}
                />
                {elements.length === 0 && !props.orphans && (
                    <>
                        {props.orphanArticlesExist ? (
                            <NoResultsRow text={t('products.nestedTable.noCategories')} />
                        ) : (
                            <NoResultsRow text={t('products.nestedTable.noResults')} />
                        )}
                    </>
                )}
            </InlineDataTable>
        </ExpansionProvider>
    )
}

export default withRouter(ProductListsTable)
